// The Internet Casino — addresses, the drand client, and the read/write helpers the cabinets use.
//
// Everything here runs in the player's browser against the public RPC and the player's own
// wallet. There is no casino server: a bet is a transaction, a settlement is a transaction, and
// the number that decides both comes from drand, which we do not run.

import { FN, ERC20, TOPIC } from './casino-abi';
import { MSFT } from '../data/msft';
import { read, rpc, sendTx, pow10, parseUnits, formatUnits } from './eth';
import {
  blueScreenUptimeBps,
  coinFacesOf,
  dialUpPortOf,
  diceRollOf,
  minesChanceWad,
  minesMaskOf,
  minesPayoutMultBps,
  roulettePocketOf,
  slotsStopOf,
  wheelSegmentOf,
} from './casino-math';

// ── deployment ───────────────────────────────────────────────────────────────
// Filled in by casino/tools/deploy.ts at deploy time. While these are zero the lobby renders
// in its dark state and every cabinet refuses to take a bet — the same gate the coin itself
// shipped behind before launch.
export const ADDRESSES = {
  house: '0xbab243fea8a47fac4c8089636bbccde3b45fc5b5',
  beacon: '0x2b0765dc135f0e25d65cce38c1f45eeb9e83a00c',
  blueScreen: '0xf99148f8e7473c2e2e842f8658e22785e0edebad',
  coinFlip: '0xd33b0e26bf9c00ab34ace00ff56c3c0088be5feb',
  dialUp: '0xa6237744272d7f1d4cc2ddd81ccd66556d3c226e',
  mines: '0xc68c628283ffe96d3cdf456412c13ea609aacba0',
  roulette: '0xb3fcaa619932879b3c83af2e81bea167a7164f5d',
  dice: '0xf20671a985d5ec7eff4a0a774047a2f9c8fa90e1',
  wheel: '0xa23e973c935cdba7ec7d6b6ff51dc14df0aacf64',
  slots: '0x6f55205ad2d8a067019dd824116ac0c31864a3c2',
};

export const CHIP = { address: MSFT, symbol: 'MSFT', decimals: 18 };

export const LIVE = Object.keys(ADDRESSES).every(
  k => ADDRESSES[k] !== '0x0000000000000000000000000000000000000000',
);

// ── practice mode ────────────────────────────────────────────────────────────
// While the floor is dark the cabinets can run on play money. Same formulas (lib/casino-math.js
// mirrors the contracts and casino/test/practice.test.ts proves it against the bytecode), same
// public beacon, no wallet, no contract, no signature, nothing on chain. It exists only in builds
// made with REACT_APP_CASINO_PRACTICE=1 (the casino-test preview) — never on production, where
// nothing of the casino ships until the addresses are filled in — and it never applies once they
// are.
const PRACTICE_BALANCE_KEY = 'ic:casino:practice:balance';
const PRACTICE_ID_KEY = 'ic:casino:practice:id';
export const PRACTICE_START = pow10(18) * BigInt(1000); // 1,000 play chips

export const PRACTICE = !LIVE && process.env.REACT_APP_CASINO_PRACTICE === '1';

/** A practice house: 100,000 play chips in the bankroll, 2% exposure — the same shape as the real one. */
export function practiceHouse() {
  const bankroll = pow10(18) * BigInt(100000);
  return { bankroll, maxPayout: (bankroll * BigInt(200)) / BigInt(10000), paused: false, practice: true };
}

export const PRACTICE_MIN_ALL_IN = pow10(15); // 0.001 chips, what casino/tools/deploy.ts sets

export function practiceBalance() {
  try {
    const v = localStorage.getItem(PRACTICE_BALANCE_KEY);
    return v === null ? PRACTICE_START : BigInt(v);
  } catch (e) {
    return PRACTICE_START;
  }
}

export function setPracticeBalance(v) {
  try {
    localStorage.setItem(PRACTICE_BALANCE_KEY, BigInt(v).toString());
  } catch (e) {
    /* private mode: the stack lives for this page only */
  }
}

/** Bet ids count up per cabinet from 0, like _bets.length does on chain. */
export function nextPracticeId(cabinetKey) {
  const key = `${PRACTICE_ID_KEY}:${cabinetKey}`;
  let n = 0;
  try {
    n = Number(localStorage.getItem(key) || '0');
    localStorage.setItem(key, String(n + 1));
  } catch (e) {
    /* private mode */
  }
  return BigInt(n);
}

export const CABINETS = [
  {
    key: 'blueScreen',
    name: 'THE BLUE SCREEN',
    tag: 'crash',
    line: 'Set the multiple you bail out at. If uptime reaches it, you cash out. If it blue-screens first, you do not.',
    edge: 'Board pays 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
  {
    key: 'mines',
    name: 'MINESWEEPER: HARD MODE',
    tag: 'mines',
    line: 'Bury the mines, open the squares you dare. One beacon round reveals the whole board.',
    edge: 'Board pays 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
  {
    key: 'dialUp',
    name: 'DIAL-UP',
    tag: 'plinko',
    line: 'A packet falls through eight rows of routers and lands on a port. 56k on the edges.',
    edge: 'Ladder pays 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
  {
    key: 'coinFlip',
    name: 'COINFLIP.EXE',
    tag: 'coinflip',
    line: 'One to four coins, all of them your side. Pays exactly 2^N.',
    edge: 'The coins are fair. The 5% fee is the house’s entire edge.',
    fair: true,
  },
  {
    key: 'roulette',
    name: 'ROULETTE.EXE',
    tag: 'roulette',
    line: 'One wheel, one zero, thirty-seven pockets. Cover a number, a colour, a dozen or any shape you like.',
    edge: 'Wheel pays 97.30% (the zero) · 5% fee · ~92.4% expected return',
    fair: false,
  },
  {
    key: 'dice',
    name: 'DICE.EXE',
    tag: 'dice',
    line: 'Roll under your number, 0.90 to 89.99. Slide it up for a safer roll, down for a bigger one.',
    edge: 'Board pays 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
  {
    key: 'wheel',
    name: 'THE WHEEL',
    tag: 'wheel',
    line: 'Fifty segments, one pointer, up to ten spins on a ticket. Nothing to choose but how much.',
    edge: 'Wheel pays 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
  {
    key: 'slots',
    name: 'JACKPOT.EXE',
    tag: 'slots',
    line: 'Three reels of Windows icons. Three globes is the jackpot and pays 100x.',
    edge: 'Reels pay 90.00% · 5% fee · ~85.5% expected return',
    fair: false,
  },
];

// ── drand quicknet ───────────────────────────────────────────────────────────

export const QUICKNET_CHAIN_HASH =
  '52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971';
export const QUICKNET_GENESIS = 1692803367;
export const QUICKNET_PERIOD = 3;

const DRAND_MIRRORS = [
  'https://api.drand.sh',
  'https://drand.cloudflare.com',
  'https://api2.drand.sh',
  'https://api3.drand.sh',
];

export function roundAt(unixSeconds) {
  if (unixSeconds <= QUICKNET_GENESIS) return 1;
  return Math.floor((unixSeconds - QUICKNET_GENESIS) / QUICKNET_PERIOD) + 1;
}

export function timeOfRound(round) {
  return QUICKNET_GENESIS + (round - 1) * QUICKNET_PERIOD;
}

/** How long one mirror gets to answer before it is abandoned. A hung request must never stall a bet. */
const DRAND_TIMEOUT_MS = 4000;

/** A round never changes once published, so one fetched in this session is never fetched again. */
const ROUND_CACHE = new Map();

/**
 * Fetch one round from whichever public mirror answers first. Every mirror is asked at once and
 * the first good answer wins; the rest are abandoned. A mirror that hangs, or answers 425 because
 * the round is not out yet, costs only its own request — never the bet's time.
 */
export async function fetchRound(round) {
  const cached = ROUND_CACHE.get(round);
  if (cached) return cached;
  const canAbort = typeof AbortController !== 'undefined';
  const controllers = [];
  const attempts = DRAND_MIRRORS.map(mirror => {
    const controller = canAbort ? new AbortController() : null;
    if (controller) controllers.push(controller);
    const timer = setTimeout(() => controller && controller.abort(), DRAND_TIMEOUT_MS);
    const url = `${mirror}/${QUICKNET_CHAIN_HASH}/public/${round}`;
    return fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        if (!json || !json.signature) throw new Error('malformed round');
        return json;
      })
      .finally(() => clearTimeout(timer));
  });
  return new Promise((resolve, reject) => {
    let pending = attempts.length;
    let lastError = null;
    attempts.forEach(attempt =>
      attempt.then(
        json => {
          if (!ROUND_CACHE.has(round)) ROUND_CACHE.set(round, json);
          controllers.forEach(c => c.abort()); // the race is over; drop the slower mirrors
          resolve(json);
        },
        err => {
          lastError = err;
          pending -= 1;
          if (pending === 0) reject(lastError || new Error('drand unreachable'));
        },
      ),
    );
  });
}

// ── BLS12-381 G1 decompression ───────────────────────────────────────────────
// drand publishes a 48-byte compressed point; the EIP-2537 precompiles take the 128-byte
// uncompressed form. Recovering y is one modular square root, because p = 3 (mod 4).
//
// This is not a trust step. A wrong y simply fails the on-chain pairing, so the worst a broken
// decompression can do is make a settlement revert — it can never fake an outcome.

const FP =
  BigInt('0x1a0111ea397fe69a4b1ba7b6434bacd764774b84f38512bf6730d2a0f6b0f6241eabfffeb153ffffb9feffffffffaaab');
const B = BigInt(4);

function modPow(base, exp, mod) {
  let result = BigInt(1);
  let b = base % mod;
  let e = exp;
  while (e > BigInt(0)) {
    if (e & BigInt(1)) result = (result * b) % mod;
    b = (b * b) % mod;
    e >>= BigInt(1);
  }
  return result;
}

const SQRT_EXP = (FP + BigInt(1)) / BigInt(4);
const HALF = (FP - BigInt(1)) / BigInt(2);

function hexOf(n, bytes) {
  return n.toString(16).padStart(bytes * 2, '0');
}

/** 48-byte compressed G1 (hex, no 0x) → 128-byte uncompressed EIP-2537 point (0x-prefixed). */
export function uncompressG1(compressedHex) {
  const hex = compressedHex.replace(/^0x/, '');
  if (hex.length !== 96) throw new Error('expected a 48-byte compressed point');

  const flags = parseInt(hex.slice(0, 2), 16);
  if ((flags & 0x80) === 0) throw new Error('not a compressed point');
  if (flags & 0x40) throw new Error('point at infinity');
  const wantLargest = (flags & 0x20) !== 0;

  const x = BigInt('0x' + hexOf(BigInt(flags & 0x1f), 1) + hex.slice(2));
  if (x >= FP) throw new Error('x out of field');

  // y^2 = x^3 + 4 over Fp. Written as repeated multiplication: `**` on BigInt is banned here.
  const x3 = (((x * x) % FP) * x) % FP;
  const y2 = (x3 + B) % FP;
  let y = modPow(y2, SQRT_EXP, FP);
  if ((y * y) % FP !== y2) throw new Error('point not on curve');
  if (y > HALF !== wantLargest) y = FP - y;

  return '0x' + '0'.repeat(32) + hexOf(x, 48) + '0'.repeat(32) + hexOf(y, 48);
}

/** The uncompressed signature for a round, ready to hand to settle(). */
export async function signatureForRound(round) {
  const r = await fetchRound(round);
  return uncompressG1(r.signature);
}

// ── reads ────────────────────────────────────────────────────────────────────

export async function houseState() {
  const [bankroll, maxPayout, paused] = await Promise.all([
    read(ADDRESSES.house, FN.House.freeBankroll, []),
    read(ADDRESSES.house, FN.House.maxPayout, []),
    read(ADDRESSES.house, FN.House.paused, []),
  ]);
  return { bankroll, maxPayout, paused };
}

export async function chipBalance(account) {
  if (!account) return BigInt(0);
  return read(CHIP.address, ERC20.balanceOf, [account]);
}

export async function allowanceFor(account) {
  if (!account) return BigInt(0);
  return read(CHIP.address, ERC20.allowance, [account, ADDRESSES.house]);
}

/** One approval covers every cabinet, because the House is the only contract that moves chips. */
export async function approve(account, amount) {
  return sendTx(account, CHIP.address, ERC20.approve, [ADDRESSES.house, amount]);
}

export async function betOf(cabinetKey, id) {
  const game = ADDRESSES[cabinetKey];
  const fn = FN[cabinetName(cabinetKey)].betAt;
  const raw = await read(game, fn, [id]);
  return raw;
}

function cabinetName(key) {
  return {
    blueScreen: 'BlueScreen',
    coinFlip: 'CoinFlip',
    dialUp: 'DialUp',
    mines: 'Mines',
    roulette: 'Roulette',
    dice: 'Dice',
    wheel: 'Wheel',
    slots: 'Slots',
  }[key];
}

export async function maxAllIn(cabinetKey, payoutMultBps) {
  const fn = FN[cabinetName(cabinetKey)].maxAllInFor;
  return read(ADDRESSES[cabinetKey], fn, [payoutMultBps]);
}

// ── writes ───────────────────────────────────────────────────────────────────

export function placeCall(cabinetKey, args) {
  const name = cabinetName(cabinetKey);
  const fn = {
    blueScreen: FN.BlueScreen.boot,
    coinFlip: FN.CoinFlip.flip,
    dialUp: FN.DialUp.dial,
    mines: FN.Mines.sweep,
    roulette: FN.Roulette.spin,
    dice: FN.Dice.roll,
    wheel: FN.Wheel.spin,
    slots: FN.Slots.spin,
  }[cabinetKey];
  return { to: ADDRESSES[cabinetKey], fn, args, name };
}

// ── reveals ──────────────────────────────────────────────────────────────────
// Every cabinet exposes a pure view that turns a settled round's randomness back into the thing
// the player watches: the crash altitude, the mine layout, the coin faces, the packet paths.
// The animation reads these rather than recomputing anything locally, so what is drawn on screen
// is the chain's own answer and cannot drift from it.

//
// While the floor is dark (practice mode) the same views are answered by lib/casino-math.js,
// which is the contract's arithmetic line for line and is tested against the bytecode.

export async function revealBlueScreen(id, randomness) {
  if (!LIVE) return blueScreenUptimeBps(id, randomness);
  return read(ADDRESSES.blueScreen, FN.BlueScreen.uptimeBps, [id, randomness]);
}

export async function revealCoinFlip(id, coins, randomness) {
  if (!LIVE) return coinFacesOf(id, coins, randomness);
  return read(ADDRESSES.coinFlip, FN.CoinFlip.facesOf, [id, coins, randomness]);
}

export async function revealDialUp(id, packets, randomness) {
  const ports = [];
  for (let p = 0; p < packets; p += 1) {
    if (LIVE) {
      // eslint-disable-next-line no-await-in-loop
      ports.push(Number(await read(ADDRESSES.dialUp, FN.DialUp.portOf, [id, p, randomness])));
    } else {
      ports.push(dialUpPortOf(id, p, randomness));
    }
  }
  return ports;
}

export async function revealMines(id, mines, randomness) {
  if (!LIVE) return minesMaskOf(id, mines, randomness);
  return read(ADDRESSES.mines, FN.Mines.mineMaskOf, [id, mines, randomness]);
}

export async function minesQuote(mines, k) {
  if (!LIVE) return { mult: minesPayoutMultBps(mines, k), chance: minesChanceWad(mines, k) };
  const [mult, chance] = await Promise.all([
    read(ADDRESSES.mines, FN.Mines.payoutMultBps, [mines, k]),
    read(ADDRESSES.mines, FN.Mines.chanceWad, [mines, k]),
  ]);
  return { mult, chance };
}

export function dialUpLadder() {
  // Mirrors DialUp.multBps. Kept in sync by casino/test — the contract is the authority.
  return [560000, 15000, 6000, 4000, 2286, 4000, 6000, 15000, 560000];
}

export function dialUpWeights() {
  return [1, 8, 28, 56, 70, 56, 28, 8, 1];
}

export async function revealRoulette(id, randomness) {
  if (!LIVE) return roulettePocketOf(id, randomness);
  return Number(await read(ADDRESSES.roulette, FN.Roulette.pocketOf, [id, randomness]));
}

export async function revealDice(id, randomness) {
  if (!LIVE) return diceRollOf(id, randomness);
  return Number(await read(ADDRESSES.dice, FN.Dice.rollOf, [id, randomness]));
}

export async function revealWheel(id, spins, randomness) {
  const segments = [];
  for (let s = 0; s < spins; s += 1) {
    if (LIVE) {
      // eslint-disable-next-line no-await-in-loop
      segments.push(Number(await read(ADDRESSES.wheel, FN.Wheel.segmentOf, [id, s, randomness])));
    } else {
      segments.push(wheelSegmentOf(id, s, randomness));
    }
  }
  return segments;
}

/** The three reel stops (0..15) a settled round showed. */
export async function revealSlots(id, randomness) {
  const stops = [];
  for (let reel = 0; reel < 3; reel += 1) {
    if (LIVE) {
      // eslint-disable-next-line no-await-in-loop
      stops.push(Number(await read(ADDRESSES.slots, FN.Slots.stopOf, [id, reel, randomness])));
    } else {
      stops.push(slotsStopOf(id, reel, randomness));
    }
  }
  return stops;
}

/**
 * Gas for a settle. A full settle measures ~670k (the BLS pairing dominates); this is the same
 * ceiling the keeper uses. Handing it to the wallet spares the player the wallet's own estimate,
 * which is a second full simulation of the pairing before the confirm button even appears.
 */
export const SETTLE_GAS = '0x124f80'; // 1,200,000

export async function settle(account, cabinetKey, id, signature) {
  const fn = FN[cabinetName(cabinetKey)].settle;
  return sendTx(account, ADDRESSES[cabinetKey], fn, [id, signature], { gas: SETTLE_GAS });
}

export { parseUnits, formatUnits, pow10 };


// ── the keeper ───────────────────────────────────────────────────────────────
//
// A worker that submits the beacon proof so the player does not have to sign a second time.
// Entirely optional: with no REACT_APP_KEEPER_URL the casino behaves exactly as it did before,
// and the player settles their own bet. The keeper is an accelerator, never a dependency —
// settle() has no access control, so anyone can close a bet and the payout still goes to the
// address stored on it.
export const KEEPER_URL = (process.env.REACT_APP_KEEPER_URL || '').replace(/\/$/, '');

/**
 * Ask the keeper to settle now. The endpoint is SYNCHRONOUS: it submits the transaction and
 * waits, so an ok response means the bet is already closed on chain. Returns true only in that
 * case — a rate-limited or failed keeper returns false and the player signs immediately rather
 * than waiting on something that is not coming. Never throws.
 */
export async function askKeeper(cabinetKey, id) {
  if (!KEEPER_URL) return false;
  try {
    // The keeper answers synchronously and retries through the RPC's throttling before it
    // does, so a bad minute can hold this open for 30s+. Cap it: past 6s the settler on its
    // 15s cron is the better bet anyway, and the chain watcher catches whichever lands.
    const signal =
      typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(6000)
        : undefined;
    const res = await fetch(KEEPER_URL + '/settle', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ game: cabinetKey, id: String(id) }),
      signal,
    });
    if (!res.ok) return false;
    const body = await res.json();
    return Boolean(body && body.ok && (body.settled || body.alreadyClosed));
  } catch (e) {
    return false; // a keeper that is down must never break a bet
  }
}

/**
 * Watch the chain for this bet closing, whoever closes it. Resolves with the Settled log, or
 * null if nobody settled inside the window — in which case the caller signs it themselves.
 */
export async function waitForSettled(cabinetKey, id, timeoutMs) {
  const address = ADDRESSES[cabinetKey];
  const idHex = '0x' + id.toString(16).padStart(64, '0');
  const limit = Date.now() + (timeoutMs || 45000);
  // This RPC refuses fromBlock: 'earliest' (it wants a hex quantity), and a silently-failing
  // poll here looked exactly like "nobody has settled yet" — the UI sat on "settling" while the
  // bet had been closed for a minute. Anchor to a concrete recent block instead. Blocks are 0.1s,
  // so 30,000 back is ~50 minutes, comfortably wider than any settle window including GRACE.
  let fromBlock = null;
  for (;;) {
    try {
      if (fromBlock === null) {
        const head = BigInt(await rpc('eth_blockNumber', []));
        const from = head > BigInt(30000) ? head - BigInt(30000) : BigInt(0);
        fromBlock = '0x' + from.toString(16);
      }
      const logs = await rpc('eth_getLogs', [
        { address, topics: [TOPIC.Settled, idHex], fromBlock, toBlock: 'latest' },
      ]);
      if (logs && logs.length) return logs[0];
    } catch (e) {
      // A network or RPC hiccup is worth retrying. A ReferenceError or TypeError is a bug in
      // this file, and retrying it silently for two minutes is exactly how a missing import
      // masqueraded as "nobody has settled yet" for an entire evening. Let those surface.
      if (e instanceof ReferenceError || e instanceof TypeError) throw e;
    }
    if (Date.now() > limit) return null;
    await new Promise(r => setTimeout(r, 1500));
  }
}
